import React, { useEffect } from 'react';
import { Globe, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Centered language modal.
 * Opens from the language pill in the sticky header. Presents all 13 supported
 * languages in a single grid so the user can pick without scrolling.
 */
const LANGS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'fa', label: 'فارسی', flag: '🇮🇷' },
  { code: 'ur', label: 'اردو', flag: '🇵🇰' },
  { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'bn', label: 'বাংলা', flag: '🇧🇩' },
];

const TITLES = {
  en: 'Choose Language', ar: 'اختر اللغة', ur: 'زبان منتخب کریں', zh: '选择语言',
  fa: 'انتخاب زبان', tr: 'Dil Seçin', fr: 'Choisir la langue', bn: 'ভাষা নির্বাচন করুন',
  id: 'Pilih Bahasa', es: 'Elige idioma', pt: 'Escolha o idioma',
  de: 'Sprache wählen', it: 'Scegli la lingua',
};

const LanguageModal = ({ open, onClose }) => {
  const { i18n } = useTranslation();
  const current = i18n.language || 'en';
  const title = TITLES[current] || TITLES.en;

  // ESC closes the modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const pick = (code) => {
    i18n.changeLanguage(code);
    onClose();
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      style={{ background: 'rgba(3, 7, 18, 0.72)', backdropFilter: 'blur(6px)' }}
      data-testid="language-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl border p-5"
        style={{
          background: 'linear-gradient(180deg, #14213D 0%, #0B132B 100%)',
          borderColor: 'rgba(245, 158, 11, 0.25)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        }}
        data-testid="language-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Globe size={18} style={{ color: '#F59E0B' }} />
            <h3 className="font-semibold text-base" style={{ color: '#F1F5F9' }}>
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-white/10 transition-all"
            data-testid="language-modal-close"
            aria-label="Close"
          >
            <X size={18} style={{ color: '#94A3B8' }} />
          </button>
        </div>

        {/* Language list */}
        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
          {LANGS.map((lang) => {
            const active = lang.code === current;
            return (
              <button
                key={lang.code}
                onClick={() => pick(lang.code)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all hover:bg-white/5"
                style={{
                  background: active ? 'rgba(245, 158, 11, 0.12)' : 'transparent',
                  border: '1px solid ' + (active ? 'rgba(245, 158, 11, 0.4)' : 'rgba(255,255,255,0.05)'),
                }}
                data-testid={`language-modal-option-${lang.code}`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-lg">{lang.flag}</span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: active ? '#F59E0B' : '#E5E7EB' }}
                  >
                    {lang.label}
                  </span>
                </span>
                {active && <Check size={16} style={{ color: '#F59E0B' }} />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LanguageModal;
